import { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    useMediaQuery,
    Typography,
    Container,
    Grid,
    Snackbar,
    Alert
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import logo from './img/logo.png';

// Validation Schema using Yup
const validationSchema = yup.object({
    password: yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one digit")
        .matches(/[!@#$%^&*]/, "Password must contain at least one special character (!@#$%^&*)"),
});

const AddPassword = () => {
    const { id, token } = useParams();
    const navigate = useNavigate();
    const isNonMobile = useMediaQuery("(min-width:600px)");
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [validUrl, setValidUrl] = useState(false);
    const param = useParams();
    const [passwordVisible, setPasswordVisible] = useState(false);
    
    useEffect(() => {
        const verifyUrl = async () => {
            try {
                await axios.get(`https://my-pers-thing-server.onrender.com/auth/password-reset/${param.id}/${param.token}`);
                setValidUrl(true);
            } catch (error) {
                setValidUrl(false);
                setSnackbar({
                    open: true,
                    message: "Invalid or expired link",
                    severity: "error",
                });
            }
        };
        verifyUrl();
    }, [param.id, param.token]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formik = useFormik({
        initialValues: {
            password: "",
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                const res = await axios.post(`https://my-pers-thing-server.onrender.com/auth/password-reset/${param.id}/${param.token}`, {
                    password: values.password,
                });
                setSnackbar({ open: true, message: res.data.message, severity: "success" });
                resetForm();

                // Redirect to login after 4 seconds
                setTimeout(() => navigate("/login"), 4000);
            } catch (err) {
                setSnackbar({
                    open: true,
                    message: err.response?.data?.message || "Something went wrong",
                    severity: "error",
                });
            } finally {
                setSubmitting(false);
            }
        },
    });

    return validUrl ? (
        <Container maxWidth="xl" sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            pt: { xs: 2, md: 4 }, // Added more padding-top for medium screens
            mt: { xs: 0, md: 0 }  // Removed negative margin
        }}>
            <style>
                {`
                    body {
                        margin: 0;
                        padding: 0;
                        overflow-x: hidden;
                    }
                `}
            </style>
            
            {/* Snackbar for showing error/success messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{
                        width: "100%",
                        fontSize: "16px",
                        borderRadius: "12px",
                        "& .MuiAlert-icon": {
                            fontSize: "24px",
                        },
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            
            <Box sx={{ 
                textAlign: "center", 
                mt: { xs: 2, md: 5 },  // Increased top margin for title on medium screens
                mb: { xs: 1, md: 2 }   // Added more bottom margin
            }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: "20px", sm: "22px", md: "25px" },
                        color: "#3B3B3B",
                        textTransform: "uppercase",
                        mb: 1
                    }}
                >
                    A Social Platform to Showcase & Discover Talent!
                </Typography>

                <Typography
                    variant="h6"
                    sx={{
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        color: "#555555",
                        fontWeight: "normal",
                    }}
                >
                    Singing 🎤, Rapping 🎶, Dancing 💃, Acting 🎭, Magic ✨, Comedy 😂, Art 🎨, Photography 📸, Instrumental 🎸, and more!
                </Typography>
            </Box>

            <Grid container spacing={10} sx={{ // Reduced spacing between grid items
                flexGrow: 1, 
                alignItems: 'center',
                justifyContent: 'center',
                mt: { xs: -1, md: -6 } // Further negative margin to shift up
            }}>
                {/* Logo Column - Only visible on larger screens */}
                {!isSmallScreen && (
                    <Grid item xs={12} md={5} sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        mt: { md: -4 } // Shift logo upward on medium screens
                    }}>
                        <Box sx={{ maxWidth: '200px', width: '100%' }}>
                            <img 
                                src={logo}
                                alt="App Logo" 
                                style={{ width: '100%', height: 'auto', borderRadius: '10px' }} 
                            />
                        </Box>
                    </Grid>
                )}
                
                {/* Form Column */}
                <Grid item xs={12} sm={10} md={6} sx={{ mt: { md: -4 } }}> {/* Shift form upward on medium screens */}
                    <form onSubmit={formik.handleSubmit}>
                        <Box
                            sx={{ 
                                width: '100%',
                                minWidth: { sm: '500px' },
                                maxWidth: '700px',
                                margin: '0 auto',
                                padding: { xs: '20px', sm: '30px' },
                                border: '1px solid #ddd',
                                borderRadius: '24px',
                                backgroundColor: '#fff',
                                mt: { xs: 2, md: 0 }
                            }}
                        >
                            {/* Mobile logo display */}
                            {isSmallScreen && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    mb: 3,
                                    maxWidth: '100px',
                                    mx: 'auto'
                                }}>
                                    <img 
                                        src={logo}
                                        alt="App Logo" 
                                        style={{ width: '100%', height: 'auto', borderRadius: '10px' }} 
                                    />
                                </Box>
                            )}

                            <Typography 
                                variant="h5" 
                                fontWeight="bold" 
                                textAlign="center" 
                                sx={{ mb: 3 }}
                            >
                                Create New Password
                            </Typography>

                            <Box
                                display="grid"
                                gap="20px"
                                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                            >
                                <TextField
                                    label={
                                        <>
                                            Password<span style={{ color: 'red' }}>*</span>
                                        </>
                                    }
                                    type={passwordVisible ? 'text' : 'password'}
                                    name="password"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.password}
                                    error={Boolean(formik.touched.password) && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                    InputProps={{
                                        endAdornment: (
                                            <span
                                                style={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginRight: '10px',
                                                    color: '#999'
                                                }}
                                                onClick={() => setPasswordVisible((prev) => !prev)}
                                            >
                                                {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                                            </span>
                                        ),
                                    }}
                                    sx={{
                                        gridColumn: "span 4",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "50px", 
                                            '& fieldset': {
                                                borderColor: '#ccc',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#FF61D2',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#FF61D2',
                                                borderWidth: '2px',
                                            },
                                            '& input[type="password"]::-ms-reveal, & input[type="password"]::-ms-clear': {
                                                display: 'none',
                                            },
                                            '& input[type="password"]::-webkit-credentials-auto-fill-button': {
                                                display: 'none',
                                            },
                                            '& input[type="password"]::-webkit-textfield-decoration-container': {
                                                display: 'none',
                                            }
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: '#999',
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: '#FF61D2',
                                        },
                                    }}
                                />
                            </Box>
                            
                            {/* Buttons */}
                            <Box>
                                <Button
                                    fullWidth
                                    type="submit"
                                    disabled={formik.isSubmitting}
                                    sx={{
                                        m: "1.5rem 0",
                                        p: "0.75rem",
                                        background: "linear-gradient(135deg, #FF61D2 0%, #FE9090 100%)",
                                        color: "#fff",
                                        borderRadius: "50px",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #FE9090 0%, #FF61D2 100%)",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                        "&.Mui-disabled": {
                                            background: "#ddd",
                                            color: "#999"
                                        }
                                    }}
                                >
                                    {formik.isSubmitting ? "Changing..." : "Submit"}
                                </Button>

                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                    <Typography
                                        onClick={() => navigate("/login")}
                                        sx={{
                                            color: "#555555",
                                            fontSize: "14px",
                                            "&:hover": {
                                                cursor: "pointer",
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        Back to Login
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </form>
                </Grid>
            </Grid>
        </Container>
    ) : (
        <Container maxWidth="xl" sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            pt: { xs: 2, md: 2 },
            mt: { xs: 0, md: 0 }
        }}>
            <Box 
                sx={{
                    p: 4,
                    borderRadius: '12px',
                    border: '1px solid #f44336',
                    backgroundColor: '#fff',
                    textAlign: 'center',
                    maxWidth: '600px'
                }}
            >
                <Typography variant="h4" color="error" gutterBottom>
                    404 - Link Expired or Invalid
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    The password reset link you tried to use is either invalid or has expired.
                </Typography>
                <Button
                    variant="contained"
                    sx={{
                        mt: 3,
                        background: "linear-gradient(135deg, #FF61D2 0%, #FE9090 100%)",
                        color: "#fff",
                        borderRadius: "50px",
                        p: "0.75rem 1.5rem",
                        "&:hover": {
                            background: "linear-gradient(135deg, #FE9090 0%, #FF61D2 100%)",
                        },
                    }}
                    onClick={() => navigate("/forgot-password")}
                >
                    Request New Link
                </Button>
            </Box>
        </Container>
    );
};

export default AddPassword;